import { apiFetch } from './client';
import { getAuthToken } from '../../utils/getAuthToken';
import type {
  Survey,
  SurveyInput,
  SurveyStatus,
  SurveyResults,
  SurveyResponsesPage,
  SurveyAnswers,
} from '../../types/survey';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// ---- End user: take a survey ----

/** Active surveys the current user can take, latest first, each with a `has_responded` flag. */
export async function getActiveSurveys(): Promise<Survey[]> {
  const res = await apiFetch('/surveys/active');
  return res.surveys || [];
}

/** Submit a response to a survey. `answers` is keyed by question id. */
export async function submitSurveyResponse(
  surveyId: number,
  answers: SurveyAnswers,
): Promise<{ responseId: number }> {
  const res = await apiFetch(`/surveys/${surveyId}/respond`, {
    method: 'POST',
    body: JSON.stringify({ answers }),
  });
  return { responseId: res.responseId };
}

// ---- Admin: CRUD ----

export async function listSurveys(): Promise<Survey[]> {
  const res = await apiFetch('/surveys');
  return res.surveys || [];
}

export async function getSurvey(id: number): Promise<Survey> {
  const res = await apiFetch(`/surveys/${id}`);
  return res.survey;
}

export async function createSurvey(input: SurveyInput): Promise<Survey> {
  const res = await apiFetch('/surveys', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return res.survey;
}

export async function updateSurvey(id: number, input: Partial<SurveyInput>): Promise<Survey> {
  const res = await apiFetch(`/surveys/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  return res.survey;
}

export async function setSurveyStatus(id: number, status: SurveyStatus): Promise<Survey> {
  const res = await apiFetch(`/surveys/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
  return res.survey;
}

export async function deleteSurvey(id: number, hard = false): Promise<void> {
  await apiFetch(`/surveys/${id}${hard ? '?hard=true' : ''}`, { method: 'DELETE' });
}

// ---- Admin: results & responses ----

export async function getSurveyResults(id: number): Promise<SurveyResults> {
  return apiFetch(`/surveys/${id}/results`);
}

export async function getSurveyResponses(
  id: number,
  limit = 50,
  offset = 0,
): Promise<SurveyResponsesPage> {
  return apiFetch(`/surveys/${id}/responses?limit=${limit}&offset=${offset}`);
}

/**
 * Download the CSV export. apiFetch always parses JSON, so we fetch directly
 * (with the auth token) and trigger a browser download from the blob.
 */
export async function exportSurveyCsv(id: number, filename?: string): Promise<void> {
  const token = await getAuthToken();
  const res = await fetch(`${BASE_URL}/surveys/${id}/results/export`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error('Failed to export survey responses');
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `survey_${id}_responses.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
